import { FileText, ChevronRight } from "lucide-react";

/**
 * ScenarioBriefRenderer — Styled panel for scenario/case study briefs
 * 
 * Expected item shape:
 * {
 *   type: "scenario_brief",
 *   title: "Scenario: Legacy System Migration",
 *   context: "You are a senior developer at Acme Corp...",
 *   sections: [
 *     { heading: "Background", content: "The current system..." },
 *     { heading: "Requirements", content: "1. Maintain uptime..." },
 *     { heading: "Constraints", content: "Budget: $50k..." }
 *   ],
 *   time_estimate: "15 minutes" (optional)
 * }
 */
export function ScenarioBriefRenderer({ item, index }) {
  const title = item.title || "Scenario Brief";
  const context = item.context || item.content || "";
  const sections = item.sections || item.supporting_data || [];
  const timeEstimate = item.time_estimate || "";

  return (
    <div key={index} className="my-6" data-testid={`scenario-brief-${index}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1B3A5C] to-[#2a5a8c] text-white px-5 py-4 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded bg-white/10 flex items-center justify-center flex-shrink-0">
            <FileText className="w-4 h-4" />
          </div>
          <h3 className="font-semibold text-base">{title}</h3>
        </div>
        {timeEstimate && (
          <span className="text-xs bg-white/15 px-3 py-1 rounded-full">
            Est. {timeEstimate}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="border border-t-0 border-[#1B3A5C]/20 rounded-b-lg bg-white">
        {/* Context paragraph */}
        {context && (
          <div className="px-5 py-4 border-b border-gray-100">
            <p className="text-[#3b3b3b] text-sm leading-relaxed">{context}</p>
          </div>
        )}

        {/* Sections */}
        {sections.map((section, sIdx) => (
          <div
            key={sIdx}
            className="px-5 py-4 border-b border-gray-100 last:border-b-0"
            data-testid={`scenario-section-${index}-${sIdx}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <ChevronRight className="w-4 h-4 text-[#00AEEF]" />
              <h4 className="font-semibold text-sm text-[#1B3A5C]">{section.heading || section.title}</h4>
            </div>
            <div className={`ml-6 text-sm text-[#3b3b3b] leading-relaxed ${section.format === "monospace" ? "font-mono bg-[#1e1e2e] text-gray-200 rounded p-3 text-xs" : "whitespace-pre-line"}`}>
              {section.content}
            </div>
            {/* Data table support */}
            {section.headers && section.rows && (
              <div className="ml-6 mt-2 overflow-x-auto">
                <table className="w-full text-sm border border-gray-200 rounded">
                  <thead>
                    <tr className="bg-gray-50">
                      {section.headers.map((h, hi) => (
                        <th key={hi} className="px-3 py-1.5 text-left text-xs font-semibold text-[#1B3A5C] border-b">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.map((row, ri) => (
                      <tr key={ri} className="border-b last:border-b-0">
                        {row.map((cell, ci) => (
                          <td key={ci} className="px-3 py-1.5 text-xs text-[#3b3b3b]">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
